/**
 * ============================================================================
 * useRisksQuery — Phase 7 frontend (Quality Risk Management Module, ICH Q9).
 * Owner: Phase 7 — risk-svc.
 * Purpose: TanStack Query hook backing the RiskRegister table view.
 *          GET /api/v1/risks returns a paged response of RiskListItem; the
 *          UI sorts client-side on the RPN column.
 *
 * URS rows: RSK-009 (list view), RSK-004 (RPN bands).
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type RiskCategory =
  | 'PRODUCT_QUALITY'
  | 'PATIENT_SAFETY'
  | 'DATA_INTEGRITY'
  | 'REGULATORY'
  | 'SUPPLY'
  | 'EQUIPMENT'
  | 'CYBER'
  | 'OTHER';

export type RiskLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export type RiskStatus = 'OPEN' | 'MITIGATING' | 'MONITORED' | 'CLOSED' | 'ACCEPTED';

export type TreatmentKind = 'MITIGATE' | 'TRANSFER' | 'ACCEPT' | 'AVOID';

export interface RiskListItem {
  riskId: string;
  riskNumber: string;
  title: string;
  riskCategory: RiskCategory;
  processScope: string;
  riskOwnerUserId: string;
  severityScore: number;
  occurrenceScore: number;
  detectabilityScore: number;
  rpn: number;
  riskLevel: RiskLevel;
  status: RiskStatus;
  treatmentKind: TreatmentKind;
  relatedSupplierId: string | null;
  relatedEquipmentId: string | null;
  nextReviewDueUtc: string | null;
  lastReviewedUtc: string | null;
  createdAtUtc: string;
}

export interface RisksPage {
  items: RiskListItem[];
  total: number;
}

export interface RisksQueryParams {
  category?: RiskCategory;
  level?: RiskLevel;
  status?: RiskStatus;
  owner?: string;
}

export function useRisksQuery(params: RisksQueryParams = {}): UseQueryResult<RisksPage> {
  return useQuery<RisksPage>({
    queryKey: ['risks', params],
    queryFn: () => apiFetch<RisksPage>('/api/v1/risks', { query: params as Record<string, string | undefined> }),
    staleTime: 30_000,
  });
}
